"use client";
import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

interface VideoPlayerProps {
    videoSource?: string;
    title?: string;
    description?: string;
    btnLabel?: string;
    btnLink?: string;
}

interface HeroVideo2Props {
    data?: {
        title?: string;
        subtitle?: string;
        btnLink?: string;
        btnText?: string;
        videos?: VideoPlayerProps[];
        isLive?: boolean;
    };
}

export default function HeroVideo2_Layout1({ data }: HeroVideo2Props) {
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const bgVideoRef = useRef<HTMLVideoElement>(null);
    const [isMuted, setIsMuted] = useState(true);

    const bannerVideoData = data?.videos?.[0] || null;
    const videoSources = data?.videos || [];

    useEffect(() => {
        videoRefs.current.forEach((video) => {
            if (video) {
                video.muted = true;
                video.play().catch((e) => console.log("Autoplay prevented:", e));
            }
        });

        if (bgVideoRef.current) {
            bgVideoRef.current.muted = isMuted;
            bgVideoRef.current.play().catch((e) =>
                console.warn("Background video autoplay prevented:", e)
            );
        }
    }, [isMuted]);

    const toggleMute = () => {
        setIsMuted(!isMuted);
        if (bgVideoRef.current) {
            bgVideoRef.current.muted = !isMuted;
        }
    };

    return (
        <section className="relative w-full min-h-screen bg-[var(--bg-col-1)] overflow-hidden">
            {/* Background video */}
            {bannerVideoData?.videoSource && (
                <div className="absolute inset-0 z-0">
                    <video
                        ref={bgVideoRef}
                        className="w-full h-full object-cover"
                        autoPlay
                        loop
                        muted={isMuted}
                        playsInline
                    >
                        <source src={bannerVideoData.videoSource} type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-black/50"></div>
                </div>
            )}


            <div className="container mx-auto px-6 py-16 relative z-20">
                {/* Header */}
                {data?.title && (
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        viewport={{ once: true }}
                        className="text-center mb-16 pt-32"
                    >
                        <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-9xl  font-light text-[var(--text-inverse)] mb-8 leading-tight tracking-wide">
                            {data.title.split(",").map((part, index) => (
                                <span
                                    key={index}
                                    className={`block ${index === 0
                                        ? " font-anton text-[var(--primary-dark)] font-normal"
                                        : "mt-2 font-light"
                                        }`}
                                >
                                    {part.trim()}
                                </span>
                            ))}
                        </h1>

                        {data?.subtitle && (
                            <p className="text-xl text-[var(--text-3)] max-w-3xl mx-auto leading-relaxed font-light tracking-wider">
                                {data.subtitle}
                            </p>
                        )}
                    </motion.div>
                )}

                {/* Video collage section */}
                {videoSources.length > 1 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        viewport={{ once: true }}
                        className="relative w-full max-w-6xl mx-auto mb-20 bg-transparent p-10 "
                    >


                        <div className="grid grid-cols-12 gap-6 mt-8">
                            {videoSources.slice(1).map((video, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.15, duration: 0.6 }}
                                    viewport={{ once: true }}
                                    className={`relative group overflow-hidden ${index === 0
                                        ? "col-span-12 h-96"
                                        : index === 1
                                            ? "col-span-8 h-80"
                                            : index === 2
                                                ? "col-span-4 h-80"
                                                : index === 3
                                                    ? "col-span-5 h-64"
                                                    : index === 4
                                                        ? "col-span-7 h-64"
                                                        : "col-span-6 h-64"
                                        }`}
                                >
                                    {video?.videoSource && (
                                        <>
                                            <video
                                                className="w-full h-full object-cover"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                ref={(el) => (videoRefs.current[index] = el)}
                                            >
                                                <source src={video.videoSource} type="video/mp4" />
                                            </video>

                                            {/* Overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
                                                <div className="p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                                    {video.title && (
                                                        <h3 className="  italic text-2xl text-[var(--text-3)] mb-2">
                                                            {video.title}
                                                        </h3>
                                                    )}
                                                    {video.description && (
                                                        <p className="text-[var(--text-2)] font-light text-sm">
                                                            {video.description}
                                                        </p>
                                                    )}
                                                    {video.btnLabel && video.btnLink && (
                                                        <Link
                                                            href={video.btnLink}
                                                            className="
      inline-block mt-3 px-4 py-2 rounded-full font-medium text-sm
      bg-[var(--btn-bg-2)] text-[var(--btn-text-2)]
      hover:bg-[var(--btn-bg-1)] hover:text-[var(--btn-text-1)]
      transition-all duration-200
    "
                                                        >
                                                            {video.btnLabel} →
                                                        </Link>
                                                    )}

                                                </div>
                                            </div>
                                        </>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Second collage */}
                {videoSources.length > 1 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        viewport={{ once: true }}
                        className="relative w-full max-w-5xl mx-auto mb-20"
                    >

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {videoSources.slice(1).map((video, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1, duration: 0.5 }}
                                    viewport={{ once: true }}
                                    className="relative group overflow-hidden h-60"
                                >
                                    {video?.videoSource && (
                                        <>
                                            <video
                                                className="w-full h-full object-cover"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                            >
                                                <source src={video.videoSource} type="video/mp4" />
                                            </video>

                                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                                                <div className="transform scale-0 group-hover:scale-100 transition-transform duration-500">
                                                    <svg
                                                        className="w-12 h-12 text-[var(--decorative-1)]"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1}
                                                            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1}
                                                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* CTA */}
                {data?.btnLink && data?.btnText && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mt-20"
                    >
                        <Link href={data.btnLink} passHref>
                            <button className="px-12 py-5 bg-transparent border border-[var(--border-1)] text-[var(--btn-text-2)] font-light tracking-widest rounded-none hover:bg-[var(--btn-hover-bg-2)] hover:text-[var(--btn-text-1)] transition-all duration-500 text-lg uppercase">
                                {data.btnText}
                                <svg
                                    className="w-5 h-5 ml-3 inline"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="1.5"
                                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                                    />
                                </svg>
                            </button>
                        </Link>
                    </motion.div>
                )}
            </div>
        </section>
    );
}
